import { useState, useEffect, useMemo } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { useDrawerDrag } from '../../../hooks/useDrawerDrag';
import { TrendingUp, CircleDollarSign, WalletCards, BanknoteArrowDown, X, CircleCheck, TriangleAlert, ArrowRight, ChevronDown, Plus } from 'lucide-react';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import StatCard from '../../../components/StatCard/StatCard';
import Button from '../../../components/Button/Button';
import Pagination from '../../../components/Pagination/Pagination';
import PageFooter from '../../../components/PageFooter/PageFooter';
import StatusTooltip from '../../../components/StatusTooltip/StatusTooltip';
import api from '../../../services/api';
import styles from './Wallet.module.css';

const ACCOUNTS_PAGE_SIZE = 5;
const WITHDRAWALS_PAGE_SIZE = 5;

const PROFIT_SHARE = 0.8; // DFY: trader keeps 80%, PB share is 20%
const STATUS_LABEL = { paid: 'Paid', processing: 'Processing', denied: 'Denied' };

/* ── Formatting helpers ── */
const money2 = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${(Math.round(Number(n || 0)) || 0).toLocaleString('en-US')}`;
const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US')}`;
const profitLabel = (n) => (Number(n) > 0 ? `+${money2(n)}` : money2(0));
const compact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;
  return money0(v);
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date} · ${time}`;
}

// DFY payout window runs the 21st–29th of each month (UTC), the same rule the API
// enforces on POST /wallet/payout. While it's open the page shows when it ends;
// otherwise when it next opens (this month's 21st, or next month's after the 29th).
const WINDOW_OPEN_DAY = 21;
const WINDOW_CLOSE_DAY = 29;

function payoutWindow(now = new Date()) {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();
  const formatDay = (y, m, d) => {
    const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return new Date(Date.UTC(y, m, Math.min(d, lastDay)))
      .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };
  if (day >= WINDOW_OPEN_DAY && day <= WINDOW_CLOSE_DAY) {
    return { open: true, label: `Window ends: ${formatDay(year, month, WINDOW_CLOSE_DAY)}` };
  }
  const opensMonth = day > WINDOW_CLOSE_DAY ? month + 1 : month;
  return { open: false, label: `Window opens: ${formatDay(year, opensMonth, WINDOW_OPEN_DAY)}` };
}

// The admin flow settles a withdrawal as 'paid' or, on denial, 'not-paid' — the
// transaction enum has no 'denied'. The rest are defensive for legacy rows.
const DENIED_STATUSES = ['not-paid', 'not-received', 'not-transferred', 'denied', 'declined', 'rejected', 'failed', 'cancelled', 'canceled'];

function normalizeStatus(s) {
  const v = (s || '').toLowerCase();
  if (v === 'paid') return 'paid';
  if (DENIED_STATUSES.includes(v)) return 'denied';
  return 'processing';
}

function formatAmountInput(raw) {
  if (!raw) return '';
  const [intPart, decPart] = raw.split('.');
  const formattedInt = intPart ? Number(intPart).toLocaleString('en-US') : '';
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
}

function cleanAmountInput(raw) {
  let clean = raw.replace(/[^\d.]/g, '');
  const firstDot = clean.indexOf('.');
  if (firstDot !== -1) {
    clean = clean.slice(0, firstDot + 1) + clean.slice(firstDot + 1).replace(/\./g, '');
  }
  return clean;
}

function StatusBadge({ status }) {
  const s = normalizeStatus(status);
  return (
    <StatusTooltip label={STATUS_LABEL[s]}>
      <span className={`${styles.badge} ${styles[`badge_${s}`]}`}>
        <span className={`${styles.badgeDot} ${styles[`dot_${s}`]}`} />
        <span className={styles.badgeLabel}>{STATUS_LABEL[s]}</span>
      </span>
    </StatusTooltip>
  );
}

const EMPTY_PAYOUT = { source: '', amount: '' };

function Wallet() {
  usePageTitle('Wallet');
  const [accountPage, setAccountPage]       = useState(1);
  const [withdrawalPage, setWithdrawalPage] = useState(1);
  const [payoutModal, setPayoutModal]       = useState(null); // null | 'form' | 'confirm' | 'submitted'
  const [payoutForm, setPayoutForm]         = useState(EMPTY_PAYOUT);
  const [payoutErrors, setPayoutErrors]     = useState({});
  const [submitting, setSubmitting]         = useState(false);
  const [submitError, setSubmitError]       = useState('');

  /* ── Server data ── */
  const [summary, setSummary]         = useState(null);
  const [accounts, setAccounts]       = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      api.get('/wallet/summary').then(r => r.data).catch(() => null),
      api.get('/wallet/accounts').then(r => r.data).catch(() => []),
      api.get('/wallet/withdrawals').then(r => r.data).catch(() => []),
    ]).then(([s, a, w]) => {
      if (!active) return;
      setSummary(s);
      setAccounts(Array.isArray(a) ? a : []);
      setWithdrawals(Array.isArray(w) ? w : []);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const isEmpty = !loading && accounts.length === 0;
  // Outside the 21st–29th the API refuses payouts, so the button is off too
  const payoutWin = payoutWindow();
  const payoutDisabled = isEmpty || !payoutWin.open;
  const selectedAccount = useMemo(
    () => accounts.find(a => String(a.login) === String(payoutForm.source)),
    [accounts, payoutForm.source]
  );

  // Accounts with a withdrawal still being processed. The server flags them on
  // /wallet/accounts; the withdrawal history is a fallback so the lock still holds
  // if an older API build doesn't send the flag.
  const pendingLogins = useMemo(() => {
    const set = new Set();
    for (const a of accounts) if (a.pendingWithdrawal) set.add(String(a.login));
    for (const w of withdrawals) {
      if (w.login != null && normalizeStatus(w.status) === 'processing') set.add(String(w.login));
    }
    return set;
  }, [accounts, withdrawals]);

  const isAccountLocked = (login) => pendingLogins.has(String(login));
  const selectedAccountLocked = !!selectedAccount && isAccountLocked(selectedAccount.login);

  const accountTotalPages = Math.max(1, Math.ceil(accounts.length / ACCOUNTS_PAGE_SIZE));
  const accountCurrentPage = Math.min(accountPage, accountTotalPages);
  const accountRows = accounts.slice((accountCurrentPage - 1) * ACCOUNTS_PAGE_SIZE, accountCurrentPage * ACCOUNTS_PAGE_SIZE);

  const withdrawalTotalPages = Math.max(1, Math.ceil(withdrawals.length / WITHDRAWALS_PAGE_SIZE));
  const withdrawalCurrentPage = Math.min(withdrawalPage, withdrawalTotalPages);
  const withdrawalRows = withdrawals.slice((withdrawalCurrentPage - 1) * WITHDRAWALS_PAGE_SIZE, withdrawalCurrentPage * WITHDRAWALS_PAGE_SIZE);

  const amountNum = parseFloat(payoutForm.amount);
  const netPayout = !isNaN(amountNum) ? amountNum * PROFIT_SHARE : 0;
  const pbShare = !isNaN(amountNum) ? amountNum - netPayout : 0;
  const sourceName = selectedAccount?.accountName || '';

  function openPayout() { setPayoutForm(EMPTY_PAYOUT); setPayoutErrors({}); setSubmitError(''); setPayoutModal('form'); }
  function closePayout() { setPayoutModal(null); }

  async function confirmPayout() {
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.post('/wallet/payout', { login: Number(payoutForm.source), amount: parseFloat(payoutForm.amount) });
      // Refresh both: the new pending request must lock this account right away.
      const [a, w] = await Promise.all([
        api.get('/wallet/accounts').then(r => r.data).catch(() => null),
        api.get('/wallet/withdrawals').then(r => r.data).catch(() => null),
      ]);
      if (Array.isArray(a)) setAccounts(a);
      if (Array.isArray(w)) setWithdrawals(w);
      setPayoutModal('submitted');
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Failed to submit payout request.');
    } finally {
      setSubmitting(false);
    }
  }

  const { modalRef: payoutDrawerRef, dragHandleProps: payoutDragHandle } = useDrawerDrag(closePayout);

  function setPayoutField(k, v) {
    setPayoutForm(f => ({ ...f, [k]: v }));
    if (payoutErrors[k]) setPayoutErrors(e => ({ ...e, [k]: '' }));
  }

  function validatePayoutForm() {
    const e = {};
    if (!payoutForm.source) { e.source = 'Source account required'; return e; }
    if (selectedAccountLocked) {
      e.source = 'A payout on this account is still being processed';
      return e;
    }
    const amt = parseFloat(payoutForm.amount);
    const available = selectedAccount?.availableToWithdraw ?? 0;
    if (!payoutForm.amount || isNaN(amt)) e.amount = 'Amount required';
    else if (amt < 500) e.amount = 'Minimum of $500 required';
    else if (selectedAccount && amt > available) e.amount = `Exceeds available to withdraw (${money2(available)})`;
    return e;
  }

  function submitPayoutForm() {
    const e = validatePayoutForm();
    if (Object.keys(e).length) { setPayoutErrors(e); return; }
    setPayoutModal('confirm');
  }

  return (
    <DashboardLayout>
      <div className={styles.walletPage}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Wallet</h1>
            <p className={styles.subtitle}>Overview of your profits, balances, and payouts.</p>
          </div>
          <div className={styles.headerRight}>
            <Button variant="primary" size="sm" fullWidth={false} onClick={openPayout} disabled={payoutDisabled} style={payoutDisabled ? { opacity: 0.6 } : undefined}><Plus size={16} /> Request payout</Button>
            <p className={styles.windowEnds}>{payoutWin.label}</p>
          </div>
        </div>

        {/* Summary */}
        <p className={styles.sectionLabel}>SUMMARY</p>
        <div className={`row g-3 mb-4 ${styles.statsRow}`}>
          <div className={`col-md-3 col-12 ${styles.statsCol}`}>
            <StatCard label="AUM" value={isEmpty ? '-' : compact(summary?.aum)} icon={TrendingUp} info tooltip={{
              text: "The total balance across all active funded accounts currently connected to your profile"
            }} />
          </div>
          <div className={`col-md-3 col-12 ${styles.statsCol}`}>
            <StatCard label="Cumulative Profit" value={isEmpty ? '-' : money0(summary?.cumulativeProfit)} icon={CircleDollarSign} info tooltip={{
              text: "Lifetime realized profits across all accounts, including withdrawn payouts"
            }} />
          </div>
          <div className={`col-md-3 col-12 ${styles.statsCol}`}>
            <StatCard label="Active Accounts" value={isEmpty ? '-' : String(summary?.activeAccounts ?? 0)} icon={WalletCards} info tooltip={{
              text: "The number of currently active funded accounts linked to your profile"
            }} />
          </div>
          <div className={`col-md-3 col-12 ${styles.statsCol}`}>
            <StatCard label="Net Withdrawals" value={isEmpty ? '-' : money0(summary?.netWithdrawals)} icon={BanknoteArrowDown} info tooltip={{
              text: "The total payouts received after your 80% profit split and deduction of applicable payout processing fees"
            }} />
          </div>
        </div>

        {/* Trading Accounts */}
        <p className={styles.sectionLabel}>YOUR TRADING ACCOUNTS</p>
        <div className={styles.tableWrap}>
          <table className={`${styles.table} ${accounts.length === 0 ? styles.tableEmpty : ''}`}>
            <thead>
              <tr>
                <th className={styles.th}>Account name</th>
                <th className={`${styles.th} ${styles.hideOnMobile}`}>Balance</th>
                <th className={`${styles.th} ${styles.hideOnMobile}`}>Realized profit</th>
                <th className={styles.th}>Available to withdraw</th>
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0
                ? <tr><td colSpan={4} className={styles.emptyCell}>No synced accounts yet</td></tr>
                : accountRows.map(a => (
                  <tr key={a.login} className={styles.row}>
                    <td className={`${styles.td} ${styles.tdBold}`}>
                      {a.accountName}
                    </td>
                    <td className={`${styles.td} ${styles.hideOnMobile}`}>{money2(a.balance)}</td>
                    <td className={`${styles.td} ${styles.hideOnMobile} ${a.realizedProfit > 0 ? styles.profitPos : ''}`}>{profitLabel(a.realizedProfit)}</td>
                    <td className={styles.td}>
                      {money2(a.availableToWithdraw)}
                      {isAccountLocked(a.login) && <span className={styles.pendingNote}>Payout processing</span>}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
          {accounts.length > 0 && <Pagination page={accountCurrentPage} totalPages={accountTotalPages} onPageChange={setAccountPage} variant="outline" />}
        </div>

        {/* Withdrawal History */}
        <p className={`${styles.sectionLabel} ${styles.sectionLabelGap}`}>WITHDRAWAL HISTORY</p>
        <div className={styles.tableWrap}>
          <table className={`${styles.table} ${withdrawals.length === 0 ? styles.tableEmpty : ''}`}>
            <thead>
              <tr>
                <th className={`${styles.th} ${styles.hideOnMobile}`}>Date</th>
                <th className={styles.th}>Account name</th>
                <th className={styles.th}>Amount</th>
                <th className={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.length === 0
                ? <tr><td colSpan={4} className={styles.emptyCell}>No withdrawal history</td></tr>
                : withdrawalRows.map(w => (
                  <tr key={w.id} className={styles.row}>
                    <td className={`${styles.td} ${styles.hideOnMobile}`}>{fmtDate(w.date)}</td>
                    <td className={`${styles.td} ${styles.tdBold}`}>
                      {w.accountName || '-'}
                    </td>
                    <td className={styles.td}>{money2(w.amount)}</td>
                    <td className={styles.td}><StatusBadge status={w.status} /></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
          {withdrawals.length > 0 && <Pagination page={withdrawalCurrentPage} totalPages={withdrawalTotalPages} onPageChange={setWithdrawalPage} variant="outline" />}
        </div>

        <PageFooter />

        {/* ── Request payout: Form ── */}
        {payoutModal === 'form' && (
          <div className={styles.overlay} onClick={closePayout}>
            <div className={styles.modal} ref={payoutDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...payoutDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Request payout</h2>
                <button className={styles.closeBtn} onClick={closePayout}><X size={20} /></button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>Source account</label>
                  <div className={styles.selectWrap}>
                    <select
                      className={`${styles.fieldInput} ${payoutErrors.source ? styles.fieldInputError : ''}`}
                      value={payoutForm.source}
                      onChange={e => setPayoutField('source', e.target.value)}
                    >
                      <option value="">Select source</option>
                      {accounts.map(a => (
                        <option key={a.login} value={a.login} disabled={isAccountLocked(a.login)}>
                          {a.accountName}{isAccountLocked(a.login) ? ' — payout processing' : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} className={styles.selectChevron} />
                  </div>
                  {payoutErrors.source && <p className={styles.fieldError}>{payoutErrors.source}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>Amount</label>
                  <div className={`${styles.amountWrap} ${payoutErrors.amount ? styles.amountWrapError : ''}`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={styles.amountInput}
                      placeholder="Min. $500"
                      value={formatAmountInput(payoutForm.amount)}
                      onChange={e => setPayoutField('amount', cleanAmountInput(e.target.value))}
                    />
                    <button
                      type="button"
                      className={styles.maxBtn}
                      disabled={!selectedAccount}
                      onClick={() => selectedAccount && setPayoutField('amount', String(Math.floor(selectedAccount.availableToWithdraw)))}
                    >
                      MAX: {selectedAccount ? money0(selectedAccount.availableToWithdraw) : '-'}
                    </button>
                  </div>
                  {payoutErrors.amount && <p className={styles.fieldError}>{payoutErrors.amount}</p>}
                </div>

                {selectedAccountLocked && (
                  <div className={styles.warningBanner}>
                    <TriangleAlert size={16} className={styles.warningIcon} />
                    A payout on this account is still being processed. You can request another one once it's completed.
                  </div>
                )}
              </div>

              <div className={`${styles.modalFooter} ${styles.formFooter}`}>
                <Button variant="secondary" size="sm" fullWidth={false} onClick={closePayout}>Cancel</Button>
                <Button variant="primary" size="sm" fullWidth={false} onClick={submitPayoutForm} disabled={selectedAccountLocked}>
                  Next <ArrowRight size={14} />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Request payout: Confirmation ── */}
        {payoutModal === 'confirm' && (
          <div className={styles.overlay} onClick={closePayout}>
            <div className={styles.modal} ref={payoutDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...payoutDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Request payout</h2>
                <button className={styles.closeBtn} onClick={closePayout}><X size={20} /></button>
              </div>

              <div className={styles.modalBody}>
                <p className={styles.modalSectionLabel}>CONFIRMATION</p>

                <div className={styles.confirmBox}>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Source account</span>
                    <span className={styles.confirmValue}>{sourceName}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Payout request</span>
                    <span className={styles.confirmValue}>{fmt(amountNum)}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>PB share (20%)</span>
                    <span className={styles.confirmValue}>-{fmt(pbShare)}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Net payout</span>
                    <span className={`${styles.confirmValue} ${styles.netPayout}`}>{fmt(netPayout)}</span>
                  </div>
                </div>

                <div className={styles.warningBanner}>
                  <TriangleAlert size={16} className={styles.warningIcon} />
                  A 20% profit share is deducted from each DFY withdrawal.
                </div>

                {submitError && <p className={styles.fieldError}>{submitError}</p>}
              </div>

              <div className={`${styles.modalFooter} ${styles.confirmFooter}`}>
                <Button variant="secondary" size="sm" fullWidth={false} onClick={() => setPayoutModal('form')} disabled={submitting}>Previous</Button>
                <Button variant="primary" size="sm" fullWidth={false} onClick={confirmPayout} disabled={submitting}>{submitting ? 'Submitting…' : 'Confirm'}</Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Request payout: Submitted ── */}
        {payoutModal === 'submitted' && (
          <div className={styles.overlay} onClick={closePayout}>
            <div className={styles.modal} ref={payoutDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...payoutDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Payout request submitted</h2>
                <button className={styles.closeBtn} onClick={closePayout}><X size={20} /></button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.successBanner}>
                  <CircleCheck size={18} className={styles.successIcon} />
                  <span>Your payout request been successfully submitted.<br />We'll give you updates via email.</span>
                </div>

                <p className={styles.modalSectionLabel}>DETAILS</p>

                <div className={styles.detailsList}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Source account</span>
                    <span className={styles.detailValue}>{sourceName}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Payout request</span>
                    <span className={styles.detailValue}>{fmt(amountNum)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>PB share (20%)</span>
                    <span className={styles.detailValue}>-{fmt(pbShare)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Net payout</span>
                    <span className={`${styles.detailValue} ${styles.netPayout}`}>{fmt(netPayout)}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

export default Wallet;
